import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Menu, X, ArrowLeft, ChevronRight, Loader2 } from 'lucide-react';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import ContainerLayout from '@/shared/components/ContainerLayout';
import { Customer } from '@/shared/types/customerTypes';
import { Organization } from '@/shared/types/userTypes';
import { CustomerDashboardTab } from '@/domains/customers/components/tabs/CustomerDashboardTab';
import {
  CustomerDetailsTab,
  getCustomerTabFromSlug,
  getVisibleCustomerDetailsTabs,
  isCustomerDetailsTabAllowed,
  isValidCustomerDetailsTabSlug,
} from '@/shared/config/customer-details-tabs';
import { getCustomerDetailsPath, getCustomerServiceLineDetailsPath } from '@/shared/utils/customer-details-routes';
import { useCustomerOrganizationOverview } from '../services/customer-overview';
import { useUser } from '@/contexts/UserContext';
import { CustomerSettingsTab } from '../components/tabs/CustomerSettingsTab';
import { ROUTES } from '@/config/routeConfig';
import { sanitize } from '@/shared/utils/format-utils';
import { useUserRoles } from '@/shared/hooks/userRoles';

function getCustomerDetailsTabFromParams(tabSlug?: string): CustomerDetailsTab {
  return getCustomerTabFromSlug(tabSlug);
}

function organizationToCustomer(organization: Organization): Customer {
  return {
    customerId: organization.organizationId,
    name: organization.name,
    role: organization.role,
    isSelfServiceAllowed: organization.isSelfServiceAllowed,
  };
}

export const CustomerDetailsPage = ({ shouldAllowAccountChange }: { shouldAllowAccountChange: boolean }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerName, tabSlug } = useParams<{ customerName: string; tabSlug?: string }>();
  const { currentUser, selectedOrganization, isInitialized, isLoading } = useUser();
  const { isCustomerUser } = useUserRoles();

  const locationCustomer = (location.state?.customer ?? location.state?.organization) as
    | Customer
    | Organization
    | undefined;

  /** Prefer context selection for customer users so APIs track the switched customer immediately. */
  const customer = useMemo(() => {
    if (isCustomerUser) {
      if (selectedOrganization) {
        return organizationToCustomer(selectedOrganization);
      }

      if (customerName) {
        const fromSlug = currentUser?.organizations?.find(
          (entry) => sanitize(entry.name) === customerName,
        );
        if (fromSlug) return organizationToCustomer(fromSlug);
      }
    }

    if (!locationCustomer) return undefined;
    if ('customerId' in locationCustomer) return locationCustomer;
    return organizationToCustomer(locationCustomer);
  }, [isCustomerUser, currentUser?.organizations, selectedOrganization, customerName, locationCustomer]);

  const activeTab = getCustomerDetailsTabFromParams(tabSlug);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Avoid firing overview with a stale customerId while context is mid-switch.
  const overviewCustomerId =
    isCustomerUser && selectedOrganization
      && customer?.customerId !== selectedOrganization.organizationId
      ? undefined
      : customer?.customerId;

  const { data: organizationOverviewData } = useCustomerOrganizationOverview(overviewCustomerId);

  const handleManageServiceLine = useCallback((serviceLine: ServiceLine) => {
    if (!serviceLine.serviceLineNumber || !customer?.name) return;
    navigate(getCustomerServiceLineDetailsPath(customer.name, serviceLine.serviceLineNumber), {
      state: {
        serviceLineNumber: serviceLine.serviceLineNumber,
        customer,
      },
    });
  }, [navigate, customer]);

  const handleTabNavigate = useCallback((tab: CustomerDetailsTab) => {
    if (!customer?.name) return;
    navigate(getCustomerDetailsPath(customer.name, tab), {
      state: { customer },
    });
  }, [navigate, customer]);

  const renderTabContent = () => {
    if (!customer) return null;

    switch (activeTab) {
      case CustomerDetailsTab.DASHBOARD:
        return (
          <CustomerDashboardTab
            key={customer.customerId}
            customer={customer}
            organizationOverviewData={organizationOverviewData}
            onManageServiceLine={handleManageServiceLine}
          />
        );

      case CustomerDetailsTab.SETTINGS:
        return (
          <CustomerSettingsTab
            key={customer.customerId}
            customer={customer}
            currentUser={currentUser}
            selectedOrganization={selectedOrganization}
          />
        );

      default:
        return (
          <CustomerDashboardTab
            key={customer.customerId}
            customer={customer}
            organizationOverviewData={organizationOverviewData}
            onManageServiceLine={handleManageServiceLine}
          />
        );
    }
  };

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tabSlug || !isValidCustomerDetailsTabSlug(tabSlug)) {
    const fallbackName = customer?.name ?? customerName ?? '';
    if (!fallbackName) {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }
    return (
      <Navigate
        to={getCustomerDetailsPath(fallbackName)}
        replace
        state={customer ? { customer } : location.state}
      />
    );
  }

  if (!customer) {
    if (isCustomerUser && !selectedOrganization) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!isCustomerDetailsTabAllowed(activeTab, customer)) {
    return (
      <Navigate
        to={getCustomerDetailsPath(customer.name, CustomerDetailsTab.DASHBOARD)}
        replace
        state={{ customer }}
      />
    );
  }

  return (
    <ContainerLayout shouldShowSidebar={false} shouldAllowRegionChange={false} shouldAllowAccountChange={shouldAllowAccountChange}>
      {/* Header Section - Fixed */}
      <div className="glass-panel flex h-16 flex-row items-center shadow-sm">
        {!isCustomerUser && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="hover:bg-transparent"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
        <div className="flex-1 flex justify-end items-center gap-4 pr-4">
          {customer && <h2 className="text-2xl font-bold">{customer.name}</h2>}
        </div>
      </div>

      <div className="flex flex-row flex-1">
        {/* Sidebar - Fixed height, no scroll */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-80'} glass-panel flex-shrink-0 h-full overflow-hidden border-r transition-all duration-500 ease-in-out`}>
          <div className="p-4 space-y-4">
            {/* Toggle Button */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="h-8 w-8 p-0 hover:bg-muted"
              >
                {isSidebarCollapsed ? <Menu className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            </div>

            {/* Navigation Tabs */}
            <div className="space-y-2">
              {!isSidebarCollapsed && (
                <h3 className="text-sm font-semibold uppercase tracking-wider">Navigation</h3>
              )}
              <div className="space-y-2">
                {getVisibleCustomerDetailsTabs(customer).map(({ tab, icon: TabIcon }) => (
                  <Button
                    key={tab}
                    variant={activeTab === tab ? "default" : "outline"}
                    className={`${isSidebarCollapsed ? 'w-8 h-8 p-0' : 'w-full justify-start gap-2'}`}
                    onClick={() => handleTabNavigate(tab)}
                    title={isSidebarCollapsed ? tab : undefined}
                  >
                    <TabIcon className="h-4 w-4" />
                    {!isSidebarCollapsed && (
                      <>
                        {tab}
                      </>
                    )}
                    {!isSidebarCollapsed && <ChevronRight className="h-4 w-4 transition-transform ml-auto" />}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area - Scrollable */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </ContainerLayout>
  );
};

export default CustomerDetailsPage;
